import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Handshake, ShieldAlert, BookOpen, MessageSquare, CheckCircle, Send, ArrowRight, ShieldCheck, Target, TrendingUp, Users, FileCheck, MapPin, ClipboardCheck, Lightbulb, Scale, Search, Calendar, Gavel, DollarSign, Clock, Award, ExternalLink } from 'lucide-react';

const RegulatorySupport = () => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', company: '', phone: '', email: '',
    facilityType: '', supportNeeded: '', currentIssue: '', timeline: '', 
    specificRequirements: '', additionalInfo: ''
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/regulatory-support`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Thank you! Your regulatory support request has been submitted successfully. We will contact you shortly.');
        // Reset form
        setFormData({
          firstName: '', lastName: '', company: '', phone: '', email: '',
          facilityType: '', supportNeeded: '', currentIssue: '', timeline: '', 
          specificRequirements: '', additionalInfo: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again or contact us directly.');
    }
  };

  const supportServices = [
    { title: 'Regulatory Interpretation', desc: 'Expert guidance on complex regulatory requirements.', items: ['Applicability determinations', 'Regulation interpretation', 'Compliance strategy development', 'Regulatory change analysis'] },
    { title: 'Agency Interaction', desc: 'Professional representation in dealings with regulatory agencies.', items: ['Pre-application meetings', 'Permit negotiations', 'Inspection support', 'Enforcement response'] },
    { title: 'Compliance Strategy', desc: 'Long-term planning for regulatory compliance.', items: ['Compliance calendars', 'Multi-permit coordination', 'Future regulation preparation', 'Risk assessment'] },
    { title: 'Training & Education', desc: 'Staff training on regulatory requirements and procedures.', items: ['Compliance training', 'Permit condition review', 'Recordkeeping procedures', 'Emergency response training'] }
  ];

  const serviceDeliverables = [
    {
      title: 'Agency Negotiation',
      description: 'Direct technical negotiation with permit writers to ensure draft conditions are physically achievable.',
      icon: Handshake
    },
    {
      title: 'NOV & Enforcement Defense',
      description: 'Technical response strategy for Notices of Violation (NOV) and Consent Decree negotiations.',
      icon: ShieldAlert
    },
    {
      title: 'Regulatory Interpretation',
      description: 'Clarifying complex applicability determinations for NSPS (Part 60) and NESHAP (Part 63) subparts.',
      icon: BookOpen
    },
    {
      title: 'Public Comment Management',
      description: 'Managing stakeholder responses and public hearings for controversial permitting actions.',
      icon: MessageSquare
    }
  ];

  const whyItMatters = [
    { icon: ShieldCheck, title: 'Avoid Mistakes', desc: 'Prevent costly misinterpretations that lead to violations and penalties.' },
    { icon: Target, title: 'Strategic Planning', desc: 'Make informed business decisions based on regulatory requirements.' },
    { icon: TrendingUp, title: 'Stay Updated', desc: 'Keep pace with new rules, guidance documents, and enforcement trends.' },
    { icon: Users, title: 'Expert Access', desc: 'Direct access to consultants with decades of regulatory experience.' }
  ];

  const regulatoryAreas = [
    { icon: FileCheck, title: 'Clean Air Act', desc: 'Federal and state air quality regulations, NAAQS, HAPs, and NESHAPs.' },
    { icon: MapPin, title: 'Alaska DEC Requirements', desc: 'State-specific air quality standards and permitting requirements.' },
    { icon: ClipboardCheck, title: 'Title V Operating Permits', desc: 'Major source operating permit requirements and compliance.' },
    { icon: Lightbulb, title: 'New Source Review', desc: 'PSD and NNSR requirements for new and modified sources.' },
    { icon: Scale, title: 'Emission Standards', desc: 'Technology-based and health-based emission standards.' },
    { icon: Search, title: 'Monitoring & Testing', desc: 'CEMS, stack testing, and monitoring requirements.' },
    { icon: Calendar, title: 'Recordkeeping & Reporting', desc: 'Documentation and reporting obligations.' },
    { icon: Gavel, title: 'Enforcement Actions', desc: 'Understanding remediation of violations and consent decrees.' }
  ];

  const guidanceServices = [
    { title: 'Regulatory Interpretation', desc: 'Plain language explanations of complex requirements.' },
    { title: 'Applicability Determinations', desc: 'Determine which regulations apply to your operations.' },
    { title: 'Compliance Strategy', desc: 'Develop effective approaches to meet requirements.' },
    { title: 'Agency Interaction', desc: 'Interface with regulators on your behalf.' },
    { title: 'Regulatory Tracking', desc: 'Monitor rule changes and impacts to your facility.' },
    { title: 'Training & Education', desc: 'Staff training on relevant regulatory requirements.' }
  ];

  const valuePropositions = [
    { value: '$50K-500K', label: 'Violation Prevention', desc: 'Average cost of regulatory violations that can be prevented with expert guidance.' },
    { value: '40-60%', label: 'Project Savings', desc: 'Typical savings on capital projects through optimized regulatory strategies.' },
    { value: 'Months', label: 'Time Saved', desc: 'Avoid delays from regulatory misunderstandings and compliance approaches.' }
  ];

  const resources = [
    { title: 'EPA Regulations', desc: 'Federal environmental regulations and guidance.', link: 'https://www.epa.gov/laws-regulations' },
    { title: 'Alaska DEC', desc: 'State environmental regulations and permits.', link: 'https://dec.alaska.gov/' },
    { title: 'CFR Title 40', desc: 'Code of Federal Regulations - Environmental Protection.', link: 'https://www.ecfr.gov/current/title-40' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20" style={{ backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.pexels.com/photos/19825346/pexels-photo-19825346.jpeg?auto=compress&cs=tinysrgb&w=1920)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Regulatory Strategy & Agency Negotiation
            </h1>
            <p className="text-xl text-gray-100 mb-8">
              Technical representation during ADEC and EPA Region 10 negotiations. We defend your operational flexibility and manage enforcement responses.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#contact-form"
                className="inline-flex items-center px-6 py-3 bg-slate-600 text-white rounded-lg font-semibold hover:bg-slate-700 transition-colors shadow-lg"
              >
                Request Strategy Session
                <Send className="ml-2" size={20} />
              </a>
              <a
                href="#key-deliverables"
                className="inline-flex items-center px-6 py-3 bg-white text-blue-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                View Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Supporting Agencies, Communities, and Industry</h2>
            <p className="text-lg text-gray-600 mb-4 leading-relaxed">
              Environmental regulations are complex, constantly changing, and costly to misunderstand. Our regulatory support services provide ongoing assistance to help you navigate regulatory changes, maintain compliance, and respond to emerging issues.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              We serve as your environmental compliance partner, providing expert guidance whenever regulatory questions arise.
            </p>
          </div>
        </div>
      </section>

      {/* Why Regulatory Guidance Matters */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Regulatory Guidance Matters</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Environmental regulations are complex, constantly changing, and costly to misunderstand.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyItMatters.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                    <IconComponent className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Regulatory Areas We Cover */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Regulatory Areas We Cover</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {regulatoryAreas.map((area, index) => {
              const IconComponent = area.icon;
              return (
                <div key={index} className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
                    <IconComponent className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{area.title}</h3>
                  <p className="text-sm text-gray-600">{area.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We Help You Navigate */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How We Help You Navigate</h2>
            <p className="text-xl text-gray-600">Our Guidance Services</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {guidanceServices.map((service, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </div>
            ))}
          </div>

          {/* Expertise Callout */}
          <div className="max-w-4xl mx-auto mt-12">
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 p-8 rounded-xl">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">13+ Years of Regulatory Expertise</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Our team has worked closely with Alaska DEC and EPA on hundreds of regulatory matters, giving us unique insight into regulatory interpretation and enforcement priorities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Deliverables - Slate Jewel Icons */}
      <section id="key-deliverables" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Defense & Strategy Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Technical representation and enforcement defense during agency negotiations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {serviceDeliverables.map((deliverable, index) => {
              const IconComponent = deliverable.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-slate-50 to-gray-100 p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow border border-slate-200">
                  {/* Jewel Style Icon - Slate */}
                  <div className="bg-slate-700 w-16 h-16 rounded-lg flex items-center justify-center shadow-lg mb-4">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{deliverable.title}</h3>
                  <p className="text-gray-600">{deliverable.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Support Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Comprehensive Support Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {supportServices.map((service, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md border-t-4 border-slate-700">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.desc}</p>
                <ul className="space-y-2">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-slate-600 mr-2 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value of Expert Guidance */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">The Value of Expert Guidance</h2>
            <p className="text-xl text-gray-600">Understanding the ROI of regulatory consultation</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {valuePropositions.map((item, index) => (
              <div key={index} className="text-center bg-gradient-to-br from-blue-50 to-slate-50 p-8 rounded-xl shadow-sm">
                <div className="text-5xl font-black text-blue-600 mb-3">{item.value}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.label}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory Resources */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Regulatory Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {resources.map((resource, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{resource.title}</h3>
                </div>
                <p className="text-gray-600 mb-4">{resource.desc}</p>
                <a
                  href={resource.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700"
                >
                  View Resource
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact-form" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-slate-700 text-white p-8 rounded-t-xl">
              <div className="flex items-center mb-4">
                <Send className="w-10 h-10 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold">Request Agency Negotiation Support</h2>
                  <p className="text-slate-100 mt-2">Get strategic representation during ADEC and EPA negotiations</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-b-xl shadow-xl border border-gray-200">
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required placeholder="First Name *" className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500" />
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required placeholder="Last Name *" className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input type="text" name="company" value={formData.company} onChange={handleChange} required placeholder="Company *" className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500" />
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="Phone *" className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500" />
                </div>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="Email *" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500" />
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Support Needs</h3>
                <select name="facilityType" value={formData.facilityType} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 mb-4">
                  <option value="">Facility Type</option>
                  <option value="power">Power Generation</option>
                  <option value="oil-gas">Oil & Gas</option>
                  <option value="military">Military</option>
                  <option value="industrial">Industrial</option>
                  <option value="other">Other</option>
                </select>
                <select name="supportNeeded" value={formData.supportNeeded} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 mb-4">
                  <option value="">Type of Support Needed</option>
                  <option value="interpretation">Regulatory Interpretation</option>
                  <option value="agency">Agency Communication</option>
                  <option value="compliance">Compliance Strategy</option>
                  <option value="training">Training & Education</option>
                  <option value="emergency">Emergency/Immediate Issue</option>
                  <option value="ongoing">Ongoing Support</option>
                </select>
                <textarea name="currentIssue" value={formData.currentIssue} onChange={handleChange} rows="4" placeholder="Describe the regulatory issue or question" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 mb-4"></textarea>
                <input type="text" name="timeline" value={formData.timeline} onChange={handleChange} placeholder="Response timeline needed" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 mb-4" />
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specific Requirements or Concerns <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="specificRequirements"
                    value={formData.specificRequirements}
                    onChange={handleChange}
                    required
                    rows="4"
                    placeholder="e.g., Regulation interpretation needs, permit condition questions, agency correspondence support, compliance strategy concerns, etc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500"
                  ></textarea>
                </div>
                <textarea name="additionalInfo" value={formData.additionalInfo} onChange={handleChange} rows="3" placeholder="Additional information or context" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500"></textarea>
              </div>

              <div className="flex justify-center">
                <button type="submit" className="inline-flex items-center px-8 py-4 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors shadow-lg">
                  Submit Strategy Request <Send className="ml-2" size={20} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-slate-700 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Defend Your Operational Flexibility
          </h2>
          <p className="text-xl text-slate-100 mb-8">
            Strategic representation during ADEC and EPA Region 10 enforcement actions
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-slate-700 rounded-lg font-semibold hover:bg-gray-100 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105"
          >
            Get Expert Negotiation Support
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RegulatorySupport;
